/**
 * Backend ERC/DRC Check Runner
 *
 * This module provides functions to run ERC (Electrical Rule Check) and DRC (Design Rule Check)
 * validators on the backend. It uses the centralized validatorRegistry to determine which
 * validators to run based on feature flags.
 *
 * The validators that can run on the backend are those marked with `supportsBackend: true`
 * in the validatorRegistry. Some validators only need data that can be derived from
 * IDocumentData (elements, vertices, pcbLayoutNodes), while others require runtime structures
 * like PcbPrimitiveStore or PcbTracingGraph which can be provided via BackendPcbRuntimeStructures.
 */
import { generateTopLevelNetMap } from "../document/functions/netMapping";
import { generateBackendValidatorsFromFeatureFlag } from "./validatorRegistry";
/**
 * Builds the IDrcInputs needed for validators from IDocumentData.
 * When runtime structures are provided, they are used to populate the inputs
 * enabling DRC validators that require baked PCB data to run on the backend.
 *
 * @param documentData - The document data to build inputs from
 * @param runtimeStructures - Optional runtime structures from PCB baking
 * @returns IDrcInputs object suitable for backend validators
 */
function buildBackendDrcInputs(documentData, runtimeStructures) {
    // Always compute the schematic net map — ERC validators need it for pin-to-pin connectivity checks.
    // DRC validators that need the PCB net map (like AirwiresValidator) use the separate pcbNetMap field.
    const netMap = generateTopLevelNetMap(documentData.elements, documentData.routeVertices);
    const netNameByNetId = documentData.nets &&
        Object.fromEntries(Object.values(documentData.nets).map((net) => [net.hash ?? net.uid, net.label]));
    // Use baked nodes from runtime structures if available, otherwise use raw pcbLayoutNodes
    const pcbLayoutNodes = runtimeStructures?.bakedNodes ?? documentData.pcbLayoutNodes;
    // Extract shape maps from PcbPrimitiveStore if available
    const pcbPrimitiveStore = runtimeStructures?.pcbPrimitiveStore;
    const nodeShapesMap = pcbPrimitiveStore?.getAllNodePrimitivesMap() ?? new Map();
    const containerMap = pcbPrimitiveStore?.getAllContainersMap() ?? new Map();
    const shapesMap = pcbPrimitiveStore?.getFillShapesMap() ?? {};
    const nodeUidList = pcbPrimitiveStore?.getAllNodeUidsWithShapes() ?? [];
    return {
        documentUid: documentData.uid,
        documentName: documentData.name,
        documentDescription: documentData.description ?? "",
        elements: documentData.elements,
        pcbLayoutNodes,
        pcbLayoutRuleSets: documentData.pcbLayoutRuleSets,
        netMap,
        netNameByNetId,
        nodeUidList,
        nodeShapesMap,
        containerMap,
        shapesMap,
        pcbNetMap: runtimeStructures?.netMap,
        pcbPrimitiveStore: pcbPrimitiveStore ?? undefined,
        pcbTracingGraph: runtimeStructures?.tracingGraph ?? undefined,
        pcbConnectivityGraphPromise: runtimeStructures?.connectivityGraph
            ? Promise.resolve(runtimeStructures.connectivityGraph)
            : undefined,
    };
}
/**
 * Runs backend-compatible validators on document data.
 *
 * This function uses the centralized validatorRegistry to determine which validators
 * to run based on:
 * 1. Feature flag configuration (enabledCheckers)
 * 2. Whether the validator supports backend execution (supportsBackend: true)
 *
 * When runtimeStructures are provided (from bakePcbLayoutForDocument), DRC validators
 * that require baked PCB data (like TraceCornerRadiusValidator, ProtectedIntrusionsValidator)
 * can also run on the backend.
 *
 * @param documentData - The document data to check
 * @param enabledCheckers - Feature flag configuration specifying which validators are enabled
 * @param runtimeStructures - Optional runtime structures from PCB baking for DRC validators
 * @returns Promise containing the problems found by each validator
 */
export async function runBackendChecks(documentData, enabledCheckers, runtimeStructures) {
    const inputs = buildBackendDrcInputs(documentData, runtimeStructures);
    // Only include validators that require runtime structures if those structures are available
    const hasRuntimeStructures = runtimeStructures !== undefined;
    const validators = generateBackendValidatorsFromFeatureFlag(enabledCheckers, hasRuntimeStructures);
    const problemsByValidator = {};
    const errors = {};
    const allProblems = [];
    // Run all validators in parallel
    const results = await Promise.all(validators.map(async (validator) => {
        try {
            return await validator.checkForProblems(inputs);
        }
        catch (e) {
            return {
                error: `Internal error: ${(e instanceof Error ? e.message : String(e)) || "Unknown error"}`,
                problemTypeKey: validator.problemTypeKey,
                foundProblems: [],
            };
        }
    }));
    // Collect results
    for (const result of results) {
        problemsByValidator[result.problemTypeKey] = result.foundProblems;
        allProblems.push(...result.foundProblems);
        if (result.error) {
            errors[result.problemTypeKey] = result.error;
        }
    }
    return {
        problemsByValidator,
        errors,
        allProblems,
    };
}
/**
 * @deprecated Use runBackendChecks instead which respects feature flags
 *
 * Legacy function for backward compatibility.
 * Runs only MissingFootprints and ManufacturePartNumber validators without feature flags.
 *
 * @param documentData - The document data to check
 * @returns Promise containing the problems found
 */
export async function runERCChecks(documentData) {
    // Enable only the legacy validators for backward compatibility
    const legacyEnabledCheckers = {
        MissingFootprintsValidator: true,
        ManufacturePartNumberValidator: true,
    };
    const results = await runBackendChecks(documentData, legacyEnabledCheckers);
    return {
        missingFootprints: results.problemsByValidator["missing_footprints"] ?? [],
        manufacturePartNumber: results.problemsByValidator["manufacture_part_number"] ?? [],
        errors: results.errors,
    };
}
/**
 * @deprecated Use runBackendChecks instead which respects feature flags
 *
 * Legacy function for backward compatibility.
 * Returns a flat array of all problems from MissingFootprints and ManufacturePartNumber validators.
 *
 * @param documentData - The document data to check
 * @returns Promise containing all problems found
 */
export async function runAllERCChecks(documentData) {
    const results = await runERCChecks(documentData);
    return [...results.missingFootprints, ...results.manufacturePartNumber];
}
//# sourceMappingURL=runERCChecks.js.map